import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    ReferenceLine,
} from "recharts";
import { Target, ArrowUpDown } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { OPS } from "@/constants/testIds";
import { fmtNumber } from "@/lib/format";

const D = OPS.opsDashboard;

const RAG_FILL = {
    green: "var(--success)",
    amber: "var(--brand-orange)",
    red: "var(--danger)",
};

function ChartCard({ icon: Icon, title, desc, testId, empty, emptyTestId, emptyDataset, month, height, children }) {
    return (
        <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)] overflow-hidden">
            <CardContent className="p-5">
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[color:var(--brand-orange-soft)]">
                        <Icon className="h-4 w-4 text-[color:var(--brand-orange)]" />
                    </span>
                    <div>
                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{title}</h3>
                        <p className="text-[11px] text-[color:var(--ink-muted)]">{desc}</p>
                    </div>
                </div>
                {empty ? (
                    <EmptyState compact testId={emptyTestId} dataset={emptyDataset} month={month} />
                ) : (
                    <div dir="ltr" data-testid={testId} style={{ width: "100%", height }}>
                        {children}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function OpsBranchCharts({ month }) {
    const { t, lang, tn } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(false);

    const load = useCallback(async (m) => {
        setLoading(true);
        try {
            const r = await api.get("/sales/branch-scorecard", { params: { month: m } });
            setData(r.data);
            setDenied(false);
        } catch (e) {
            if (e?.response?.status === 403) setDenied(true);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (month) load(month);
    }, [month, load]);

    const branches = data?.branches || [];
    const nm = (b) => tn({ name_ar: b.branch.name_ar, name_en: b.branch.name_en });

    // Attainment: branches with a target + revenue, worst (lowest %) first
    const attainmentData = useMemo(
        () => branches
            .filter((b) => b.achievement_pct != null)
            .map((b) => ({ id: b.branch.id, name: nm(b), value: b.achievement_pct, rag: b.rag, fill: RAG_FILL[b.rag] || "var(--brand-sky)" }))
            .sort((a, b) => a.value - b.value),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [branches, lang]
    );

    // Movers: branches with a MoM delta, largest magnitude first
    const moversData = useMemo(
        () => branches
            .filter((b) => b.mom_delta_pct != null)
            .map((b) => ({ id: b.branch.id, name: nm(b), value: b.mom_delta_pct }))
            .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
            .slice(0, 8),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [branches, lang]
    );

    if (denied) return null; // caller lacks the sales tab — degrade silently

    if (loading || !data) {
        return (
            <div data-testid={D.chartsSection} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Skeleton className="h-64 w-full rounded-2xl" />
                <Skeleton className="h-64 w-full rounded-2xl" />
            </div>
        );
    }

    const attHeight = Math.max(200, attainmentData.length * 30 + 20);
    const movHeight = Math.max(200, moversData.length * 30 + 20);

    return (
        <div data-testid={D.chartsSection} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
                icon={Target}
                title={t("ops_charts_attainment_title")}
                desc={t("ops_charts_attainment_desc")}
                testId={D.attainmentChart}
                emptyTestId={D.attainmentEmpty}
                empty={attainmentData.length === 0}
                emptyDataset="sales_targets"
                month={month}
                height={attHeight}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attainmentData} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--separator)" />
                        <XAxis type="number" tickFormatter={(v) => `${fmtNumber(v, lang, 0)}%`} tick={{ fontSize: 11, fill: "var(--ink-faint)" }} />
                        <YAxis type="category" dataKey="name" width={lang === "ar" ? 130 : 118} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} interval={0} />
                        <Tooltip
                            cursor={{ fill: "rgba(224,222,217,0.35)" }}
                            formatter={(v) => [`${fmtNumber(v, lang, 0)}%`, t("sc_attainment")]}
                            contentStyle={{ borderRadius: 12, border: "1px solid var(--border-soft)", background: "var(--bg-surface)" }}
                        />
                        <ReferenceLine x={100} stroke="var(--success)" strokeDasharray="4 4" strokeWidth={1.5} />
                        <Bar dataKey="value" radius={[0, 5, 5, 0]} maxBarSize={20} isAnimationActive>
                            {attainmentData.map((d) => (<Cell key={d.id} fill={d.fill} />))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <ChartCard
                icon={ArrowUpDown}
                title={t("ops_charts_movers_title")}
                desc={t("ops_charts_movers_desc")}
                testId={D.moversChart}
                emptyTestId={D.moversEmpty}
                empty={moversData.length === 0}
                emptyDataset="sales_daily_branch"
                month={month}
                height={movHeight}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={moversData} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--separator)" />
                        <XAxis type="number" tickFormatter={(v) => `${v > 0 ? "+" : ""}${fmtNumber(v, lang, 0)}%`} tick={{ fontSize: 11, fill: "var(--ink-faint)" }} />
                        <YAxis type="category" dataKey="name" width={lang === "ar" ? 130 : 118} tick={{ fontSize: 11, fill: "var(--ink-muted)" }} interval={0} />
                        <Tooltip
                            cursor={{ fill: "rgba(224,222,217,0.35)" }}
                            formatter={(v) => [`${v > 0 ? "+" : ""}${fmtNumber(v, lang, 1)}%`, t("sc_mom")]}
                            contentStyle={{ borderRadius: 12, border: "1px solid var(--border-soft)", background: "var(--bg-surface)" }}
                        />
                        <ReferenceLine x={0} stroke="var(--ink-faint)" strokeWidth={1} />
                        <Bar dataKey="value" radius={[0, 5, 5, 0]} maxBarSize={20} isAnimationActive>
                            {moversData.map((d) => (
                                <Cell key={d.id} fill={d.value >= 0 ? "var(--success)" : "var(--danger)"} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>
        </div>
    );
}
